/**
 * Notice: the block a screen uses to say something that is not an error.
 *
 * The kit already has the error block (4.3), and it is deliberately expensive:
 * a summary, a plain cause, a fix request written as a prompt, and a raw
 * disclosure, none of them optional. That is exactly right for a thing that
 * broke and exactly wrong for the six places in this flow where the product
 * has to say something plainly and get out of the way: you did not finish
 * signing in, GitHub is not answering, this repository already has files in
 * it, you are on the list.
 *
 * Reaching for the error block in those places would have made every ordinary
 * interruption look like a fault, and would have offered a coding agent a fix
 * request for a situation no agent can fix. So this is the quiet sibling: one
 * glyph, one sentence, and at most one row of actions.
 *
 * The tone is never the message. Every notice reads correctly with all color
 * removed, because the glyph is a shape and the sentence is a sentence. The
 * attention tone is the failure tone from the pill vocabulary, flat, with no
 * glow, because nothing is running.
 */

import type { ReactNode } from 'react';
import '../../styles/components/notice.css';

export type NoticeTone = 'quiet' | 'attention' | 'done';

export interface NoticeProps {
  tone?: NoticeTone;
  /** One short line, in bold. Omit it when the body is the whole message. */
  title?: string;
  children: ReactNode;
  /** At most one row. The screen's ink block may live here. */
  actions?: ReactNode;
}

function Glyph({ tone }: { tone: NoticeTone }) {
  return (
    <span className="notice__glyph" data-tone={tone} aria-hidden="true">
      <svg viewBox="0 0 16 16" width="14" height="14" focusable="false">
        {tone === 'attention' ? (
          <>
            <path
              d="M8 2.2 15 14H1Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinejoin="round"
            />
            <path d="M8 6.6v3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="8" cy="11.9" r="0.9" fill="currentColor" />
          </>
        ) : null}
        {tone === 'done' ? (
          <path
            d="M3 8.5 6.5 12 13 4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
        {tone === 'quiet' ? (
          <>
            <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d="M8 7.2v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="8" cy="4.9" r="0.9" fill="currentColor" />
          </>
        ) : null}
      </svg>
    </span>
  );
}

export function Notice({ tone = 'quiet', title, children, actions }: NoticeProps) {
  return (
    <section className="notice" data-tone={tone}>
      <Glyph tone={tone} />
      <div className="notice__body">
        {title ? <p className="notice__title">{title}</p> : null}
        <div className="notice__text">{children}</div>
        {actions ? <div className="notice__actions">{actions}</div> : null}
      </div>
    </section>
  );
}
